import {useEffect, useRef, useState} from "react";

const extractIframeData = (embedHtml) => {
    // Create a temporary DOM parser
    const parser = new DOMParser();
    const doc = parser.parseFromString(embedHtml, "text/html");

    // Find the iframe element
    const iframe = doc.querySelector("iframe");
    if (!iframe) return null;

    // Extract src attribute
    const src = iframe.getAttribute("src");

    // Extract JSON options from src hash
    let options = null;
    if (src && src.includes("#options=")) {
        try {
            const jsonString = decodeURIComponent(src.split("#options=")[1]);
            options = JSON.parse(jsonString);
        } catch (error) {
            console.error("Error parsing iframe options:", error);
        }
    }

    return {
        src,
        srcBase: src.split("#")[0], // Remove the #options part
        options,
        widget: options?.widget || null,
        width: options?.width || "370px",
        currency: options?.currency || null,
        eventPeriod: options?.["event-period"] || null,
        channel: options?.channel || null,
        products: options?.products || null,
    };
};

// The newer embed is an empty div that TicketingHub's own script turns into a
// widget. _app loads that script and initialises it once, in onLoad, which only
// works when the div happens to already be in the page by then - this div is put
// there by an effect below, so on a slow script load, or after a Fast Refresh,
// nothing ever initialises it and the booking box stays empty. Doing it here
// instead means the embed is initialised every time it is inserted, whichever
// order the two arrive in, the way the older iframe embed already behaves.
export const initTicketingHubEmbed = (container) => {
    const widget = container.querySelector("[data-th-embed]");

    if (!widget) {
        return () => {};
    }

    let cancelled = false;

    const start = () => {
        if (cancelled || !window.TicketinghubWidget) {
            return false;
        }

        // already initialised - their script gives the iframe an id
        if (widget.querySelector("iframe")) {
            return true;
        }

        try {
            new window.TicketinghubWidget(widget, window.TicketinghubWidget.widgetConfig(widget));
        } catch (error) {
            console.error("TicketingHub init failed", error);
        }

        return true;
    };

    if (start()) {
        return () => { cancelled = true; };
    }

    // their script has not finished loading yet
    const poll = setInterval(() => {
        if (start()) {
            clearInterval(poll);
        }
    }, 100);
    const giveUp = setTimeout(() => clearInterval(poll), 10000);

    return () => {
        cancelled = true;
        clearInterval(poll);
        clearTimeout(giveUp);
    };
};

export default function TicketingHubEmbed({embed, className = "plugin"}) {
    const pluginRef = useRef(null);
    const scriptRef = useRef(null);
    const [scriptData, setScriptData] = useState(null);
    const [isIframe, setIsIframe] = useState();

    useEffect(() => {
        if (!embed) return;

        // 1) Try iframe flow (src + #options)
        const iframeData = extractIframeData(embed);
        if (iframeData) {
            setIsIframe(true);
            setScriptData(iframeData);
            return;
        }

        setIsIframe(false);
        setScriptData(embed);
    }, [embed]);

    useEffect(() => {
        if (scriptData && pluginRef.current && isIframe) {
            // Remove existing iframe(s) inside pluginRef
            pluginRef.current.querySelector("iframe")?.remove();

            // Remove this instance's previously injected script, if any
            scriptRef.current?.remove();

            // Size the widget to the actual available space so it doesn't render
            // wider than its container and force a horizontal scrollbar on mobile
            const containerWidth = pluginRef.current.clientWidth;
            const width = containerWidth > 0 ? `${Math.floor(containerWidth)}px` : scriptData.width;

            // Inject the TicketingHub checkout script dynamically
            const script = document.createElement("script");
            script.src = "https://assets.ticketinghub.com/checkout.js";
            script.setAttribute("data-widget", scriptData.widget); // Set extracted widget
            script.setAttribute("data-width", width); // Fit container instead of the CMS-configured width
            script.async = true;

            pluginRef.current.appendChild(script);
            scriptRef.current = script;
        }

        if (scriptData && pluginRef.current && !isIframe) {
            pluginRef.current.innerHTML = scriptData;

            return initTicketingHubEmbed(pluginRef.current);
        }
    }, [scriptData, isIframe]);

    return <div ref={pluginRef} className={className}></div>;
}
